import React, { useState, useMemo } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Trash2,
  AlertTriangle,
  Lock,
  ChevronDown,
  ChevronUp,
  Loader2,
  Mail,
  Check,
  Users,
  Building2
} from 'lucide-react'
import toast from 'react-hot-toast'
import { useAuthStore } from '../../stores/authStore'
import { createLogger } from '../../utils/logger'

const logger = createLogger('AccountDeletion')

const AccountDeletion = () => {
  const user = useAuthStore((state) => state.user)
  const [isExpanded, setIsExpanded] = useState(false)
  const [password, setPassword] = useState('')
  const [confirmUnderstand, setConfirmUnderstand] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)

  const isGuardian = user?.accountType === 'guardian'

  const dataToBeDeleted = useMemo(() => {
    const baseData = [
      'Profile information (name, email, preferences)',
      'All challenge sessions and responses',
      'Performance data and progress history',
      'Skill level records and achievements',
      'Feedback submissions',
      'Organization memberships',
      'Account verification data',
      'Session history'
    ]

    if (isGuardian) {
      return [
        ...baseData,
        'Your family organization and all its settings',
        'All child accounts linked to your family',
        "All children's challenge sessions and progress data",
        "All children's skill levels and achievements",
        'All pending invitations sent to children'
      ]
    }

    return baseData
  }, [isGuardian])

  const handleRequestDeletion = async () => {
    // Validation
    if (!password.trim()) {
      toast.error('Please enter your password')
      return
    }

    if (!confirmUnderstand) {
      toast.error('Please confirm you understand this action cannot be undone')
      return
    }

    setIsDeleting(true)
    try {
      const response = await fetch('/api/compliance/request-deletion', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          password,
          confirmDelete: true
        })
      })

      const data = await response.json()

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Incorrect password. Please try again.')
        }
        throw new Error(data.message || 'Failed to request account deletion')
      }

      if (data.success) {
        toast.success('Account deletion email sent! Please check your inbox.')
        setShowConfirmDialog(false)
        setPassword('')
        setConfirmUnderstand(false)
        setIsExpanded(false)
      }
    } catch (error) {
      logger.error('Error requesting deletion:', error)
      toast.error(error.message || 'Failed to request account deletion')
      // Clear password on error for security
      setPassword('')
    } finally {
      setIsDeleting(false)
    }
  }

  const handleCancel = () => {
    setShowConfirmDialog(false)
    setPassword('')
    setConfirmUnderstand(false)
  }

  return (
    <div className="card border-2 border-red-200 dark:border-red-900/50">
      {/* Header - Always visible */}
      <div className="p-6">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full flex items-center justify-between gap-4 text-left"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-lg">
              <Trash2 className="w-5 h-5 text-red-600 dark:text-red-400" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-red-900 dark:text-red-100">
                Delete Account
              </h2>
              <p className="text-sm text-red-700 dark:text-red-300 mt-0.5">
                Permanently delete your account and all data
              </p>
            </div>
          </div>
          {isExpanded ? (
            <ChevronUp className="w-5 h-5 text-gray-600 dark:text-gray-400 flex-shrink-0" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-600 dark:text-gray-400 flex-shrink-0" />
          )}
        </button>
      </div>

      {/* Expandable Content */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
          >
            <div className="px-6 pb-6 space-y-6 border-t border-red-200 dark:border-red-900/50 pt-6">
              {/* Danger Warning */}
              <div className="bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500 p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-6 h-6 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <h3 className="text-sm font-bold text-red-900 dark:text-red-100 mb-2">
                      Warning: This Action Cannot Be Undone
                    </h3>
                    <p className="text-sm text-red-800 dark:text-red-200 mb-3">
                      Deleting your account is permanent. Once confirmed, you will lose access to
                      all your data, progress, and achievements. This action cannot be reversed.
                    </p>
                    <p className="text-sm text-red-800 dark:text-red-200">
                      You will receive a confirmation email. You must click the link in that email
                      within 24 hours to complete the deletion.
                    </p>
                  </div>
                </div>
              </div>

              {/* Guardian-specific cascade deletion warning */}
              {isGuardian && (
                <div className="bg-orange-50 dark:bg-orange-900/20 border-l-4 border-orange-500 p-4">
                  <div className="flex items-start gap-3">
                    <Users className="w-6 h-6 text-orange-600 dark:text-orange-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <h3 className="text-sm font-bold text-orange-900 dark:text-orange-100 mb-2">
                        Important: Family Account Deletion
                      </h3>
                      <p className="text-sm text-orange-800 dark:text-orange-200 mb-3">
                        As a guardian account holder, deleting your account will also permanently
                        delete:
                      </p>
                      <ul className="text-sm text-orange-800 dark:text-orange-200 space-y-1 mb-3">
                        <li className="flex items-center gap-2">
                          <Building2 className="w-4 h-4 flex-shrink-0" />
                          <span>Your entire family organization</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <Users className="w-4 h-4 flex-shrink-0" />
                          <span>All child accounts and their complete learning history</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <Mail className="w-4 h-4 flex-shrink-0" />
                          <span>Any pending invitations to children</span>
                        </li>
                      </ul>
                      <p className="text-sm font-medium text-orange-900 dark:text-orange-100">
                        Child accounts cannot be transferred to another guardian. Please ensure you
                        have exported all necessary data before proceeding.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Data to be Deleted */}
              <div>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                  <Trash2 className="w-4 h-4" />
                  The following data will be permanently deleted:
                </h3>
                <ul className="space-y-2">
                  {dataToBeDeleted.map((item, index) => (
                    <li
                      key={index}
                      className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300"
                    >
                      <div className="w-1.5 h-1.5 bg-red-500 rounded-full mt-1.5 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Alternatives */}
              <div className="bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-primary-900 dark:text-primary-100 mb-2">
                  Alternatives to Consider
                </h3>
                <ul className="text-sm text-primary-800 dark:text-primary-200 space-y-1.5">
                  <li>• Temporarily disable email notifications in Communication Preferences</li>
                  <li>• Export your data first if you want to keep a copy</li>
                  <li>• Contact support if you're having issues with your account</li>
                </ul>
              </div>

              {/* Confirmation Form */}
              <div className="space-y-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                  <Lock className="w-4 h-4" />
                  Confirm Account Deletion
                </h3>

                {/* Password Input */}
                <div>
                  <label
                    htmlFor="delete-password"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                  >
                    Enter your password to continue
                  </label>
                  <input
                    id="delete-password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={isDeleting}
                    placeholder="Your password"
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                </div>

                {/* Confirmation Checkbox */}
                <label className="flex items-start gap-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={confirmUnderstand}
                    onChange={(e) => setConfirmUnderstand(e.target.checked)}
                    disabled={isDeleting}
                    className="mt-0.5 w-5 h-5 text-red-600 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-red-500 focus:ring-offset-0 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                  />
                  <span className="flex-1 text-sm text-gray-700 dark:text-gray-300 select-none">
                    {isGuardian ? (
                      <>
                        I understand that this action is permanent and cannot be undone. All my
                        data, my family organization, and all linked child accounts will be
                        permanently deleted. I will lose access to my account and all children will
                        lose access to their accounts.
                      </>
                    ) : (
                      <>
                        I understand that this action is permanent and cannot be undone. All my data
                        will be permanently deleted and I will lose access to my account.
                      </>
                    )}
                  </span>
                </label>

                {/* Action Buttons */}
                <div className="flex items-center justify-end gap-3 pt-2">
                  <button
                    onClick={() => setIsExpanded(false)}
                    disabled={isDeleting}
                    className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => setShowConfirmDialog(true)}
                    disabled={isDeleting || !password || !confirmUnderstand}
                    className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-semibold rounded-lg shadow-sm hover:shadow-md transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                  >
                    <Trash2 className="w-4 h-4" />
                    <span>Delete My Account</span>
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Final Confirmation Dialog */}
      <AnimatePresence>
        {showConfirmDialog && (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black bg-opacity-75">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="relative bg-white dark:bg-gray-800 rounded-lg shadow-2xl max-w-md w-full p-6"
            >
              <div className="flex items-start gap-4 mb-6">
                <div className="p-3 bg-red-100 dark:bg-red-900/30 rounded-full">
                  <AlertTriangle className="w-6 h-6 text-red-600 dark:text-red-400" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                    Final Confirmation
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Are you absolutely sure you want to delete your account? This will send a
                    confirmation email to <strong>{user?.email}</strong>.
                  </p>
                </div>
              </div>

              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3 mb-6">
                <p className="text-sm text-yellow-800 dark:text-yellow-200">
                  You will have 24 hours to confirm the deletion via email. If you don't confirm,
                  your account will remain active.
                </p>
              </div>

              {isGuardian && (
                <div className="bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-700 rounded-lg p-3 mb-6">
                  <p className="text-sm font-medium text-red-800 dark:text-red-200 flex items-center gap-2">
                    <Users className="w-4 h-4 flex-shrink-0" />
                    This will also delete your family organization and all linked child accounts.
                  </p>
                </div>
              )}

              <div className="flex items-center justify-end gap-3">
                <button
                  onClick={handleCancel}
                  disabled={isDeleting}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  onClick={handleRequestDeletion}
                  disabled={isDeleting}
                  className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-semibold rounded-lg shadow-sm hover:shadow-md transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                >
                  {isDeleting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Processing...</span>
                    </>
                  ) : (
                    <>
                      <Mail className="w-4 h-4" />
                      <span>Send Confirmation Email</span>
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default AccountDeletion
